import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, ArrowUp, ChevronDown, Loader2 } from "lucide-react";
import {
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { SidebarSlotContext } from "@/lib/sidebarSlot";
import {
  api,
  type CallAssessmentForCall,
  type CallAssessmentLabel,
  type CallAssessmentMeasure,
  type CallAssessmentSummary,
  type CallAssessmentSummaryStatus,
  type CallDetailFlag,
  type CallDetailSegment,
  type UserOut,
} from "@/lib/api";
import { CollapsibleSection } from "@/components/CollapsibleSection";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { Tooltip } from "../components/Tooltip";
import {
  assessmentDotClass,
  fmtDateTime,
  fmtDurationMs,
  relativeTime,
} from "../lib/format";

const FLAG_META: Record<
  CallDetailFlag,
  { label: string; cls: string }
> = {
  fast: {
    label: "Fast",
    cls: "bg-accent/20 border-accent/40 text-accent-foreground",
  },
  mono: {
    label: "Mono",
    cls: "bg-accent/20 border-accent/40 text-accent-foreground",
  },
  quality: {
    label: "Quality",
    cls: "bg-secondary/10 border-secondary/30 text-secondary",
  },
};

function cleanRecapText(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    if (!/['"]content['"]\s*:/.test(trimmed)) {
      return null;
    }
    const contentRe =
      /['"]content['"]\s*:\s*(?:'((?:\\.|[^'\\])*)'|"((?:\\.|[^"\\])*)")/g;
    const formatRe =
      /['"]format['"]\s*:\s*(?:'([^']*)'|"([^"]*)")/g;
    const contents: { raw: string; quote: '"' | "'" }[] = [];
    let cm: RegExpExecArray | null;
    while ((cm = contentRe.exec(trimmed)) !== null) {
      if (cm[1] !== undefined) contents.push({ raw: cm[1], quote: "'" });
      else if (cm[2] !== undefined) contents.push({ raw: cm[2], quote: '"' });
    }
    if (contents.length > 0) {
      const formats: string[] = [];
      let fm: RegExpExecArray | null;
      while ((fm = formatRe.exec(trimmed)) !== null) {
        formats.push(fm[1] ?? fm[2] ?? "");
      }
      let pickedIdx = contents.findIndex(
        (_, i) => formats[i] === "medium" || formats[i] === "long",
      );
      if (pickedIdx < 0) pickedIdx = 0;
      const picked = contents[pickedIdx];
      if (picked === undefined) return null;
      const { raw, quote } = picked;
      const escapedForJson =
        quote === '"'
          ? raw.replace(/\\'/g, "'")
          : raw.replace(/"/g, '\\"').replace(/\\'/g, "'");
      try {
        return JSON.parse(`"${escapedForJson}"`);
      } catch {
        return raw.replace(/\\'/g, "'").replace(/\\n/g, "\n");
      }
    }
  }
  return trimmed;
}

function Chip({
  children,
  cls,
}: {
  children: React.ReactNode;
  cls: string;
}) {
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium ${cls}`}
    >
      {children}
    </span>
  );
}

function ChipButton({
  children,
  cls,
  onClick,
  ariaLabel,
}: {
  children: React.ReactNode;
  cls: string;
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium cursor-pointer transition-colors hover:brightness-110 hover:saturate-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${cls}`}
    >
      {children}
    </button>
  );
}

export function CallDetailView({
  callId,
  onBack,
  backLabel,
  me,
}: {
  callId: string;
  onBack: () => void;
  backLabel: string;
  me: UserOut | null;
}) {
  const q = useQuery({
    queryKey: ["call-detail", callId],
    queryFn: () => api.callDetail(callId),
    enabled: !!callId,
  });

  // Browser-tab title: the call's contact name (or phone number) once
  // the call loads, with a sensible placeholder while it's resolving.
  useDocumentTitle(
    (q.error as { status?: number } | null)?.status === 404
      ? "Call not found"
      : q.data?.contact_name || q.data?.contact_phone || "Call",
  );

  return (
    <div className="p-6 space-y-4 max-w-5xl mx-auto">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="w-4 h-4" />
        {backLabel}
      </button>

      {q.isLoading ? (
        <div className="space-y-3">
          <div className="h-8 w-64 bg-muted rounded animate-pulse" />
          <div className="h-4 w-96 bg-muted rounded animate-pulse" />
          <div className="h-32 w-full bg-muted rounded animate-pulse" />
          <div className="h-64 w-full bg-muted rounded animate-pulse" />
        </div>
      ) : q.error ? (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-destructive text-sm">
          {(q.error as { status?: number; message?: string })?.status === 404
            ? "Call not found, or you don't have access to it."
            : (q.error as { message?: string })?.message ||
              "Failed to load call"}
        </div>
      ) : q.data ? (
        <CallDetailBody data={q.data} me={me} />
      ) : null}
    </div>
  );
}

type LabelChipKind =
  | { kind: "flag"; flag: CallDetailFlag; emptyNote?: string }
  | { kind: "callLevel"; emptyNote: string }
  | {
      kind: "assessmentLabel";
      assessmentId: string;
      assessmentName: string;
      measureLabel: string;
      labelName: string;
      turns: number[];
    }
  | {
      // Task #155 — measure-name click opens a modal grouped by
      // detected label_name with chunk buttons per turn.
      kind: "assessmentMeasure";
      assessmentId: string;
      assessmentName: string;
      measureLabel: string;
      detectedLabels: { name: string; turns: number[] }[];
    };

type ChipDef = {
  key: string;
  label: string;
  cls: string;
  binding: LabelChipKind;
};

function CallDetailBody({
  data,
  me,
}: {
  data: import("@/lib/api").CallDetail;
  me: UserOut | null;
}) {
  const flagChips: ChipDef[] = [];
  if (data.positive_sentiment)
    flagChips.push({
      key: "pos",
      label: "Positive sentiment",
      cls: "bg-primary/10 border-primary/30 text-primary",
      binding: {
        kind: "callLevel",
        emptyNote:
          "Positive sentiment is scored at the call level, not on individual transcript lines.",
      },
    });
  if (data.negative_sentiment)
    flagChips.push({
      key: "neg",
      label: "Negative sentiment",
      cls: "bg-destructive/10 border-destructive/30 text-destructive",
      binding: {
        kind: "callLevel",
        emptyNote:
          "Negative sentiment is scored at the call level, not on individual transcript lines.",
      },
    });
  if (data.speaking_too_quickly)
    flagChips.push({
      key: "fast",
      ...FLAG_META.fast,
      binding: { kind: "flag", flag: "fast" },
    });
  if (data.monologuing)
    flagChips.push({
      key: "mono",
      ...FLAG_META.mono,
      binding: { kind: "flag", flag: "mono" },
    });
  if (data.quality_call)
    flagChips.push({
      key: "quality",
      ...FLAG_META.quality,
      binding: { kind: "flag", flag: "quality" },
    });

  const recapChips: ChipDef[] = [];
  const cleanedDisposition = cleanRecapText(data.recap_disposition);
  if (cleanedDisposition) {
    recapChips.push({
      key: "recap-disposition",
      label: cleanedDisposition,
      cls: "bg-muted border-border text-foreground",
      binding: {
        kind: "callLevel",
        emptyNote:
          "Disposition is summarised at the call level, not tied to specific transcript lines.",
      },
    });
  }
  for (const p of data.recap_purposes) {
    recapChips.push({
      key: `recap-purpose:${p}`,
      label: p,
      cls: "bg-secondary/10 border-secondary/30 text-secondary",
      binding: {
        kind: "callLevel",
        emptyNote:
          "Call purposes are summarised at the call level, not tied to specific transcript lines.",
      },
    });
  }

  const [openChip, setOpenChip] = useState<ChipDef | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  // Transcript open by default so the conversation is visible without an
  // extra click; jumpToSegment still force-opens it before scrolling.
  const [transcriptOpen, setTranscriptOpen] = useState<boolean>(true);
  const [summaryOpen, setSummaryOpen] = useState<boolean>(true);
  const [assessmentsOpen, setAssessmentsOpen] = useState<boolean>(true);
  const summaryToggleLabel = summaryOpen ? "Hide" : "Show";
  const segmentRefs = useRef<(HTMLLIElement | null)[]>([]);
  const triggerRef = useRef<HTMLElement | null>(null);

  const openChipFromEvent = useCallback(
    (chip: ChipDef, e: React.MouseEvent<HTMLButtonElement>) => {
      triggerRef.current = e.currentTarget;
      setOpenChip(chip);
    },
    [],
  );

  const closeChipModal = useCallback(() => {
    setOpenChip(null);
    const t = triggerRef.current;
    if (t) {
      queueMicrotask(() => t.focus());
    }
  }, []);

  const dispositionChip = recapChips.find(
    (c) => c.key === "recap-disposition",
  );

  const jumpToTop = useCallback(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  const jumpToSegment = useCallback((idx: number) => {
    setTranscriptOpen(true);
    // Defer to next microtask so the collapsible has rendered before
    // we scroll — otherwise the ref is still pointing at an unmounted
    // node and scrollIntoView is a no-op.
    queueMicrotask(() => {
      const node = segmentRefs.current[idx];
      if (!node) return;
      node.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedIndex(idx);
      window.setTimeout(() => {
        setHighlightedIndex((curr) => (curr === idx ? null : curr));
      }, 1500);
    });
  }, []);

  return (
    <>
      {me?.role === "supadmin" ? (
        <CallLabelsPanel
          flagChips={flagChips}
          assessments={data.assessments_for_call}
          segments={data.segments}
          onOpenChip={openChipFromEvent}
          onJumpToTop={jumpToTop}
        />
      ) : null}
      <header className="space-y-2">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-baseline gap-3 flex-wrap min-w-0">
            <h1 className="text-2xl font-semibold text-foreground truncate">
              {data.contact_name || data.contact_phone || "Unknown caller"}
            </h1>
            <span
              className="font-mono text-xs text-muted-foreground"
              title={data.call_id}
            >
              {data.call_id}
            </span>
          </div>
          <div className="text-sm text-muted-foreground tabular-nums text-right shrink-0">
            <div>{data.started_at ? fmtDateTime(data.started_at) : "\u2014"}</div>
            <div>
              {data.duration_ms != null ? fmtDurationMs(data.duration_ms) : "\u2014"}
              {data.direction ? ` \u00b7 ${data.direction}` : ""}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          <div className="flex items-baseline gap-1.5">
            <span className="text-xs uppercase tracking-wide text-muted-foreground">
              Customer
            </span>
            <span className="text-foreground font-medium">
              {data.contact_name || data.contact_phone || "Unknown"}
            </span>
          </div>
          {(data.agent_name || data.agent_email) ? (
            <div className="flex items-baseline gap-1.5">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">
                Agent
              </span>
              <span className="text-foreground font-medium">
                {data.agent_name || data.agent_email}
              </span>
            </div>
          ) : null}
        </div>
        {(flagChips.length > 0 || dispositionChip) ? (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {flagChips.map((f) => (
              <ChipButton
                key={f.key}
                cls={f.cls}
                onClick={(e) => openChipFromEvent(f, e)}
                ariaLabel={`Show transcript segments for ${f.label}`}
              >
                {f.label}
              </ChipButton>
            ))}
            {dispositionChip ? (
              <ChipButton
                key={`hdr-${dispositionChip.key}`}
                cls={dispositionChip.cls}
                onClick={(e) => openChipFromEvent(dispositionChip, e)}
                ariaLabel={`Show transcript segments for disposition ${dispositionChip.label}`}
              >
                {dispositionChip.label}
              </ChipButton>
            ) : null}
          </div>
        ) : null}
      </header>

      <CollapsibleSection
        title={<h3 className="font-semibold text-foreground">Summary</h3>}
        rightAdornment={
          <span className="text-xs text-muted-foreground">{summaryToggleLabel}</span>
        }
        open={summaryOpen}
        onOpenChange={setSummaryOpen}
      >
        {me ? (
          <AssessmentSummaryCard
            callId={data.call_id}
            summary={data.call_summary}
            assessments={data.assessments_for_call}
          />
        ) : null}
      </CollapsibleSection>

      {me ? (
        <CollapsibleSection
          title={
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-foreground">Assessments</h3>
              <span className="text-xs text-muted-foreground tabular-nums">
                {data.assessments_for_call.length}
              </span>
            </div>
          }
          rightAdornment={
            <span className="text-xs text-muted-foreground">
              {assessmentsOpen ? "Hide" : "Show"}
            </span>
          }
          open={assessmentsOpen}
          onOpenChange={setAssessmentsOpen}
        >
          {data.assessments_for_call.length === 0 ? (
            <div className="text-sm text-muted-foreground italic">
              No assessments are assigned to any team this agent belongs to.
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {data.assessments_for_call.map((a) => (
                <AssessmentCard
                  key={a.assessment_id}
                  assessment={a}
                  onOpenMeasureDrill={(measure) => {
                    const detectedLabels = measure.labels
                      .filter((l) => l.detected && l.turns.length > 0)
                      .map((l) => ({ name: l.name, turns: l.turns }));
                    const chip: ChipDef = {
                      key: `assess:${a.assessment_id}:${measure.measure_id}`,
                      label: `${a.assessment_name} \u2014 ${measure.measure_label}`,
                      cls: "bg-muted border-border text-foreground",
                      binding: {
                        kind: "assessmentMeasure",
                        assessmentId: a.assessment_id,
                        assessmentName: a.assessment_name,
                        measureLabel: measure.measure_label,
                        detectedLabels,
                      },
                    };
                    triggerRef.current = null;
                    setOpenChip(chip);
                  }}
                  onOpenLabelDrill={(measure, lbl, e) => {
                    const chip: ChipDef = {
                      key: `assess:${a.assessment_id}:${measure.measure_id}:${lbl.name}`,
                      label: `${a.assessment_name} \u2014 ${measure.measure_label} \u2014 ${lbl.name}`,
                      cls: "bg-primary/10 border-primary/30 text-primary",
                      binding: {
                        kind: "assessmentLabel",
                        assessmentId: a.assessment_id,
                        assessmentName: a.assessment_name,
                        measureLabel: measure.measure_label,
                        labelName: lbl.name,
                        turns: lbl.turns,
                      },
                    };
                    openChipFromEvent(chip, e);
                  }}
                />
              ))}
            </div>
          )}
        </CollapsibleSection>
      ) : null}

      <CollapsibleSection
        title={
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-foreground">Transcript</h3>
            <span className="text-xs text-muted-foreground tabular-nums">
              {data.segments.length}
            </span>
          </div>
        }
        rightAdornment={
          <span className="text-xs text-muted-foreground">
            {transcriptOpen ? "Hide" : "Show"}
          </span>
        }
        open={transcriptOpen}
        onOpenChange={setTranscriptOpen}
      >
        {data.segments.length > 0 ? (
          <ol className="space-y-2">
            {data.segments.map((s, i) => {
              const flagBg =
                s.flags.length === 0
                  ? "bg-card"
                  : s.flags.includes("quality")
                    ? "bg-primary/10 border-l-primary"
                    : s.flags.includes("fast")
                      ? "bg-accent/10 border-l-accent"
                      : "bg-secondary/10 border-l-secondary";
              const isHighlighted = highlightedIndex === i;
              return (
                <li
                  key={i}
                  id={`call-segment-${i}`}
                  ref={(el) => {
                    segmentRefs.current[i] = el;
                  }}
                  className={`pl-3 py-2 border-l-2 transition-shadow duration-300 ${flagBg} ${
                    s.flags.length === 0 ? "border-l-border" : ""
                  } ${
                    isHighlighted
                      ? "ring-2 ring-ring ring-offset-2 ring-offset-card rounded"
                      : ""
                  }`}
                >
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-xs font-semibold text-foreground">
                      {s.speaker || "Unknown"}
                    </span>
                    {s.time ? (
                      <span className="text-[10px] text-muted-foreground tabular-nums">
                        {s.time}
                      </span>
                    ) : null}
                    {s.turn != null ? (
                      <span className="text-[10px] text-muted-foreground tabular-nums">
                        #{s.turn}
                      </span>
                    ) : null}
                    {s.flags.map((f) => (
                      <Chip key={f} cls={FLAG_META[f].cls}>
                        {FLAG_META[f].label}
                      </Chip>
                    ))}
                  </div>
                  {s.text ? (
                    <p className="text-sm text-foreground mt-0.5 whitespace-pre-wrap">
                      {s.text}
                    </p>
                  ) : null}
                </li>
              );
            })}
          </ol>
        ) : data.transcript_text ? (
          <pre className="text-sm text-foreground whitespace-pre-wrap font-sans">
            {data.transcript_text}
          </pre>
        ) : (
          <p className="text-sm text-muted-foreground italic">
            No transcript was captured for this call.
          </p>
        )}
      </CollapsibleSection>

      {openChip ? (
        <LabelDetailModal
          chip={openChip}
          segments={data.segments}
          onClose={closeChipModal}
          onJump={(idx) => {
            triggerRef.current = null;
            setOpenChip(null);
            window.setTimeout(() => jumpToSegment(idx), 0);
          }}
        />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------
// CallLabelsPanel — sticky left-column index of clickable labels.
// ---------------------------------------------------------------------------

function CallLabelsPanel({
  flagChips,
  assessments,
  segments,
  onOpenChip,
  onJumpToTop,
}: {
  flagChips: ChipDef[];
  assessments: CallAssessmentForCall[];
  segments: CallDetailSegment[];
  onOpenChip: (chip: ChipDef, e: React.MouseEvent<HTMLButtonElement>) => void;
  onJumpToTop: () => void;
}) {
  const dialpadItems = useMemo(() => {
    return flagChips.filter((c) => {
      if (c.binding.kind !== "flag") return false;
      const flag = c.binding.flag;
      return segments.some((s) => s.flags.includes(flag));
    });
  }, [flagChips, segments]);

  const assessmentGroups = useMemo(() => {
    return assessments
      .filter((a) => a.state === "success_with_evidence")
      .map((a) => {
        const items: {
          measure: CallAssessmentMeasure;
          label: CallAssessmentLabel;
        }[] = [];
        for (const m of a.measures) {
          for (const lbl of m.labels) {
            if (lbl.detected && lbl.turns.length > 0) {
              items.push({ measure: m, label: lbl });
            }
          }
        }
        return { assessment: a, items };
      })
      .filter((g) => g.items.length > 0);
  }, [assessments]);

  const slot = useContext(SidebarSlotContext);

  if (dialpadItems.length === 0 && assessmentGroups.length === 0) return null;
  if (!slot) return null;

  const dialpadDotColor: Record<string, string> = {
    fast: "bg-rose-400",
    mono: "bg-amber-400",
    quality: "bg-sky-400",
  };
  const panelChipCls =
    "bg-white/10 border-white/20 text-white hover:bg-white/20";

  const sections: React.ReactNode[] = [];
  if (dialpadItems.length > 0) {
    sections.push(
      <div key="__dialpad" className="space-y-2">
        <div className="flex items-center gap-2">
          <h4 className="text-[10px] font-semibold uppercase tracking-[0.12em] text-white/80">
            Dialpad
          </h4>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {dialpadItems.map((c) => {
            const flag =
              c.binding.kind === "flag" ? c.binding.flag : "";
            const dot = dialpadDotColor[flag] ?? "bg-white/60";
            return (
              <ChipButton
                key={c.key}
                cls={panelChipCls}
                ariaLabel={`Show transcript segments for ${c.label}`}
                onClick={(e) => onOpenChip(c, e)}
              >
                <span
                  className={`mr-1.5 inline-block h-1.5 w-1.5 rounded-full ${dot}`}
                  aria-hidden="true"
                />
                {c.label}
              </ChipButton>
            );
          })}
        </div>
      </div>,
    );
  }
  assessmentGroups.forEach((g) => {
    sections.push(
      <div key={g.assessment.assessment_id} className="space-y-2">
        <div className="flex items-center gap-2 min-w-0">
          <h4 className="text-[10px] font-semibold uppercase tracking-[0.12em] text-white/80 truncate">
            {g.assessment.assessment_name}
          </h4>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {g.items.map(({ measure, label }) => {
            const chip: ChipDef = {
              key: `panel:${g.assessment.assessment_id}:${measure.measure_id}:${label.name}`,
              label: `${g.assessment.assessment_name} \u2014 ${measure.measure_label} \u2014 ${label.name}`,
              cls: panelChipCls,
              binding: {
                kind: "assessmentLabel",
                assessmentId: g.assessment.assessment_id,
                assessmentName: g.assessment.assessment_name,
                measureLabel: measure.measure_label,
                labelName: label.name,
                turns: label.turns,
              },
            };
            return (
              <ChipButton
                key={chip.key}
                cls={chip.cls}
                ariaLabel={`Show transcript segments for ${label.name}`}
                onClick={(e) => onOpenChip(chip, e)}
              >
                {label.name}
                <span className="ml-1.5 rounded-sm bg-white/15 px-1 text-[10px] tabular-nums text-white/90">
                  {label.turns.length}
                </span>
              </ChipButton>
            );
          })}
        </div>
      </div>,
    );
  });

  return createPortal(
    <aside className="sticky top-2 rounded-xl border border-white/15 bg-black/20 backdrop-blur-sm shadow-lg ring-1 ring-black/10 max-h-[calc(100vh-1rem)] overflow-hidden flex flex-col">
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-white/10">
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/90">
          On this call
        </span>
        <button
          type="button"
          onClick={onJumpToTop}
          className="inline-flex items-center gap-1 rounded-md border border-white/20 bg-white/10 px-2 py-1 text-[11px] font-medium text-white hover:bg-white/20 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
          aria-label="Jump to top of call"
        >
          <ArrowUp className="h-3 w-3" aria-hidden="true" />
          Top
        </button>
      </div>
      <div className="overflow-y-auto px-3 py-3 space-y-4">
        {sections.map((s, i) => (
          <div key={i}>
            {i > 0 ? <div className="h-px bg-white/10 -mx-3 mb-4" aria-hidden="true" /> : null}
            {s}
          </div>
        ))}
      </div>
    </aside>,
    slot,
  );
}

// ---------------------------------------------------------------------------
// Assessments — Assessment Summary card (sidecar polling).
// ---------------------------------------------------------------------------

function SummaryStatusPill({ status }: { status: CallAssessmentSummaryStatus }) {
  if (status === "generating") {
    return (
      <span className="inline-flex items-center gap-1 rounded border border-input bg-muted/40 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        <span aria-hidden="true">{"\u25CF"}</span>
        {"UPDATING\u2026"}
      </span>
    );
  }
  if (status === "failed") {
    return (
      <span className="inline-flex items-center rounded border border-destructive/30 bg-destructive/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-destructive">
        UPDATE FAILED
      </span>
    );
  }
  if (status === "disabled") {
    return (
      <span className="inline-flex items-center rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        DISABLED
      </span>
    );
  }
  return null;
}

function CounterLine({
  assessments,
}: {
  assessments: CallAssessmentForCall[];
}) {
  const total = assessments.length;
  const graded = assessments.filter(
    (a) => a.state === "success_with_evidence" || a.state === "no_evidence",
  ).length;
  const failed = assessments.filter((a) => a.state === "failed").length;
  const pending = assessments.filter((a) => a.state === "not_scored").length;
  if (total === 0) return null;
  const parts: string[] = [`Based on ${graded} of ${total} assessments`];
  if (failed > 0) parts.push(`${failed} failed`);
  if (pending > 0) parts.push(`${pending} pending`);
  return (
    <div className="text-xs text-muted-foreground tabular-nums">
      {parts.join(" \u00b7 ")}
    </div>
  );
}

function KeyChips({
  assessments,
}: {
  assessments: CallAssessmentForCall[];
}) {
  type Chip = { key: string; text: string };
  const chips: Chip[] = [];
  for (const a of assessments) {
    if (a.state !== "success_with_evidence") continue;
    let detected = 0;
    let total = 0;
    for (const m of a.measures) {
      detected += m.detected_count;
      total += m.total_labels;
    }
    if (total > 0) {
      chips.push({
        key: a.assessment_id,
        text: `${a.assessment_name}: ${detected}/${total} labels`,
      });
    }
  }
  if (chips.length === 0) return null;
  const shown = chips.slice(0, 4);
  const more = chips.length - shown.length;
  return (
    <div className="flex flex-wrap gap-1.5">
      {shown.map((c) => (
        <Chip key={c.key} cls="bg-primary/10 border-primary/30 text-primary">
          <span className="tabular-nums">{c.text}</span>
        </Chip>
      ))}
      {more > 0 ? (
        <Chip cls="bg-muted border-border text-muted-foreground">
          +{more} more
        </Chip>
      ) : null}
    </div>
  );
}

function AssessmentSummaryCard({
  callId,
  summary,
  assessments,
}: {
  callId: string;
  summary: CallAssessmentSummary;
  assessments: CallAssessmentForCall[];
}) {
  const qc = useQueryClient();
  const serverGenerating = summary.status === "generating";
  const [retryCount, setRetryCount] = useState(0);
  // A manual Retry enqueues a fresh job but leaves the cache row at its
  // current status (e.g. 'failed') until the worker finishes — so we can't
  // rely on the server status flipping to 'generating' to start polling.
  // Hold the freshly-enqueued job id here and poll it directly, so one click
  // shows "Generating…" and the card auto-updates on completion (previously it
  // stayed 'failed' until a second click re-fetched the now-ready row).
  const [pendingJobId, setPendingJobId] = useState<string | null>(null);

  const generating = serverGenerating || pendingJobId !== null;
  const pollJobId = pendingJobId ?? summary.job_id;

  // Task #155 — while generating, poll the generic job-status endpoint every
  // 2s; on a terminal state, refetch the call detail (await it so the card
  // doesn't flash the stale status) and release the pending-job hold.
  useQuery({
    queryKey: ["call-summary-job-status", callId, pollJobId],
    enabled: generating && !!pollJobId,
    refetchInterval: 2000,
    queryFn: async () => {
      const job = await api.jobStatus(pollJobId as string);
      if (job.status === "succeeded" || job.status === "dead") {
        await qc.invalidateQueries({ queryKey: ["call-detail", callId] });
        setPendingJobId(null);
      }
      return job;
    },
  });

  const refreshMut = useMutation({
    mutationFn: () => api.refreshCallAssessmentSummary(callId),
    onSuccess: (data) => {
      setRetryCount((n) => n + 1);
      // Drive the generating view + polling from the freshly-enqueued job so
      // the card auto-updates on completion. Fall back to an immediate refetch
      // if the API returned no job id.
      if (data.job_id) {
        setPendingJobId(data.job_id);
      } else {
        qc.invalidateQueries({ queryKey: ["call-detail", callId] });
      }
    },
  });

  const busy = refreshMut.isPending || pendingJobId !== null;
  const showRefresh =
    !generating &&
    (summary.status === "ready" ||
      summary.status === "failed" ||
      summary.status === "no_data");

  return (
    <div className="rounded-lg border border-border bg-card/60 p-4 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground">
            Assessment Summary
          </h3>
          <SummaryStatusPill status={generating ? "generating" : summary.status} />
        </div>
        {showRefresh ? (
          <button
            type="button"
            onClick={() => refreshMut.mutate()}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded border border-input bg-card px-2 py-0.5 text-xs hover:bg-muted/40 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy
              ? "Refreshing\u2026"
              : summary.status === "failed"
                ? "Retry"
                : "Refresh"}
          </button>
        ) : null}
      </div>
      {!generating && summary.status === "ready" ? (
        <>
          <p className="text-sm text-foreground whitespace-pre-wrap">
            {summary.text}
          </p>
          <CounterLine assessments={assessments} />
          <KeyChips assessments={assessments} />
          <div className="text-xs text-muted-foreground tabular-nums">
            Updated {relativeTime(summary.updated_at)}
          </div>
        </>
      ) : generating ? (
        <div className="space-y-2">
          <div
            className="bg-muted/40 rounded h-16 animate-pulse"
            aria-hidden="true"
          />
          <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
            {"Generating summary\u2026"}
          </div>
          <CounterLine assessments={assessments} />
        </div>
      ) : summary.status === "failed" ? (
        <div className="space-y-1">
          <div className="text-sm text-destructive flex items-start gap-2">
            <AlertTriangle
              className="h-4 w-4 shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <span>{summary.error ?? "Generation failed."}</span>
          </div>
          {retryCount >= 3 ? (
            <div className="text-xs text-muted-foreground">
              {"Persistent failure \u2014 please contact support."}
            </div>
          ) : null}
        </div>
      ) : summary.status === "no_data" ? (
        <div className="text-sm text-muted-foreground">
          No assessment has been graded for this call yet.
        </div>
      ) : (
        <div className="text-xs text-muted-foreground italic">
          Call summaries are disabled.
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Assessments — per-assessment card.
// ---------------------------------------------------------------------------

function AssessmentCard({
  assessment,
  onOpenMeasureDrill,
  onOpenLabelDrill,
}: {
  assessment: CallAssessmentForCall;
  onOpenMeasureDrill: (measure: CallAssessmentMeasure) => void;
  onOpenLabelDrill: (
    measure: CallAssessmentMeasure,
    label: CallAssessmentLabel,
    e: React.MouseEvent<HTMLButtonElement>,
  ) => void;
}) {
  const [open, setOpen] = useState<boolean>(false);
  const dotCls = assessmentDotClass(assessment.assessment_id);
  const stateBadge = stateBadgeFor(assessment.state);
  return (
    <div className="rounded-lg border border-border bg-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span
            aria-hidden="true"
            className={`inline-block h-2.5 w-2.5 rounded-full ${dotCls}`}
          />
          <span className="text-sm font-medium text-foreground truncate">
            {assessment.assessment_name}
          </span>
          <Chip cls={stateBadge.cls}>{stateBadge.label}</Chip>
        </div>
        <ChevronDown
          aria-hidden="true"
          className={`h-4 w-4 text-muted-foreground transition-transform ${
            open ? "rotate-0" : "-rotate-90"
          }`}
        />
      </button>
      {open ? (
        <div className="px-4 pb-4 space-y-3">
          {assessment.state === "failed" ? (
            <div className="space-y-1">
              <div className="text-sm text-destructive flex items-start gap-2">
                <AlertTriangle
                  className="h-4 w-4 shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <span>{assessment.error ?? "Grading failed."}</span>
              </div>
              <div className="text-xs text-muted-foreground">
                Retry runs from Phase 3 in DialpadView.
              </div>
            </div>
          ) : assessment.state === "not_scored" ? (
            <div className="text-sm text-muted-foreground italic">
              Not scored yet
            </div>
          ) : assessment.state === "no_evidence" ? (
            <div className="text-sm text-muted-foreground italic">
              No relevant evidence in this call
            </div>
          ) : (
            <SuccessBody
              assessment={assessment}
              onOpenMeasureDrill={onOpenMeasureDrill}
              onOpenLabelDrill={onOpenLabelDrill}
            />
          )}
        </div>
      ) : null}
    </div>
  );
}

function stateBadgeFor(
  state: CallAssessmentForCall["state"],
): { label: string; cls: string } {
  switch (state) {
    case "success_with_evidence":
      return {
        label: "Graded",
        cls: "bg-primary/10 border-primary/30 text-primary",
      };
    case "no_evidence":
      return {
        label: "No evidence",
        cls: "bg-muted border-border text-foreground",
      };
    case "failed":
      return {
        label: "Failed",
        cls: "bg-destructive/10 border-destructive/30 text-destructive",
      };
    case "not_scored":
      return {
        label: "Not scored",
        cls: "bg-muted border-border text-muted-foreground",
      };
  }
}

// Task #155 — disabled measure button title text per evidence_status.
const EVIDENCE_DISABLED_NOTE: Record<string, string> = {
  disabled: "Evidence extraction is disabled",
  failed: "Evidence extraction failed for this assessment",
  missing: "No evidence row was produced for this assessment",
  no_spans: "Evidence extractor produced no transcript spans",
};

function SuccessBody({
  assessment,
  onOpenMeasureDrill,
  onOpenLabelDrill,
}: {
  assessment: CallAssessmentForCall;
  onOpenMeasureDrill: (measure: CallAssessmentMeasure) => void;
  onOpenLabelDrill: (
    measure: CallAssessmentMeasure,
    label: CallAssessmentLabel,
    e: React.MouseEvent<HTMLButtonElement>,
  ) => void;
}) {
  return (
    <>
      {assessment.summary ? (
        <div className="space-y-1">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Summary
          </div>
          <p className="text-sm text-foreground whitespace-pre-wrap">
            {assessment.summary}
          </p>
        </div>
      ) : null}
      {assessment.measures.length === 0 ? (
        <div className="text-xs text-muted-foreground italic">
          This assessment has no measures.
        </div>
      ) : (
        <div className="space-y-2">
          {[...assessment.measures]
            .sort((a, b) => a.order_index - b.order_index)
            .map((m) => (
              <MeasureBlock
                key={m.measure_id}
                measure={m}
                evidenceStatus={assessment.evidence_status}
                onOpenMeasureDrill={() => onOpenMeasureDrill(m)}
                onOpenLabelDrill={(lbl, e) => onOpenLabelDrill(m, lbl, e)}
              />
            ))}
        </div>
      )}
    </>
  );
}

function MeasureBlock({
  measure,
  evidenceStatus,
  onOpenMeasureDrill,
  onOpenLabelDrill,
}: {
  measure: CallAssessmentMeasure;
  evidenceStatus: CallAssessmentForCall["evidence_status"];
  onOpenMeasureDrill: () => void;
  onOpenLabelDrill: (
    label: CallAssessmentLabel,
    e: React.MouseEvent<HTMLButtonElement>,
  ) => void;
}) {
  const disabled = evidenceStatus !== "ok";
  const disabledTitle =
    evidenceStatus !== "ok"
      ? EVIDENCE_DISABLED_NOTE[evidenceStatus] ?? "Evidence not available"
      : undefined;
  return (
    <div className="rounded border border-border bg-card/60 px-3 py-2 space-y-1.5">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Tooltip
          content={
            disabled
              ? disabledTitle ?? "Evidence not available"
              : "Open transcript evidence for this measure"
          }
        >
          {disabled ? (
            // The Tooltip primitive binds focus/hover handlers to its
            // trigger; a disabled <button> doesn't reliably emit those,
            // so we render a focusable span instead for the disabled
            // state so the explanation still appears on keyboard focus.
            <span
              role="button"
              aria-disabled="true"
              tabIndex={0}
              className="text-sm font-medium text-foreground opacity-60 cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded text-left"
            >
              {measure.measure_label || "(unnamed measure)"}
            </span>
          ) : (
            <button
              type="button"
              onClick={onOpenMeasureDrill}
              className="text-sm font-medium text-foreground hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded text-left"
            >
              {measure.measure_label || "(unnamed measure)"}
            </button>
          )}
        </Tooltip>
        <span className="text-xs text-muted-foreground tabular-nums">
          {measure.detected_count}/{measure.total_labels} detected
        </span>
      </div>
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Label presence
      </div>
      {measure.labels.length === 0 ? (
        <div className="text-xs text-muted-foreground italic">
          No labels configured.
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {measure.labels.map((lbl) => {
            const interactive = lbl.detected && lbl.turns.length > 0;
            const cls = interactive
              ? "bg-primary/10 border-primary/30 text-primary"
              : "bg-muted border-border text-muted-foreground line-through";
            const content = (
              <>
                {lbl.name}
                {lbl.turns.length > 0 ? (
                  <span className="ml-1 text-[10px] tabular-nums opacity-80">
                    ({lbl.turns.length})
                  </span>
                ) : null}
              </>
            );
            if (lbl.detected && lbl.turns.length > 0) {
              return (
                <ChipButton
                  key={lbl.name}
                  cls={cls}
                  ariaLabel={lbl.name}
                  onClick={(e) => onOpenLabelDrill(lbl, e)}
                >
                  {content}
                </ChipButton>
              );
            }
            return (
              <Chip key={lbl.name} cls={cls}>
                {content}
              </Chip>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// LabelDetailModal — widened to handle assessment-label drill-through.
// ---------------------------------------------------------------------------

type ModalGroup = {
  heading: string;
  matches: { idx: number }[];
  emptyNote?: string;
};

function LabelDetailModal({
  chip,
  segments,
  onClose,
  onJump,
}: {
  chip: ChipDef;
  segments: CallDetailSegment[];
  onClose: () => void;
  onJump: (segmentIndex: number) => void;
}) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const titleId = useId();

  const groups: ModalGroup[] = useMemo(() => {
    const b = chip.binding;
    if (b.kind === "flag") {
      const out: { idx: number }[] = [];
      segments.forEach((s, idx) => {
        if (s.flags.includes(b.flag)) out.push({ idx });
      });
      return [{ heading: chip.label, matches: out, emptyNote: b.emptyNote }];
    }
    if (b.kind === "callLevel") {
      return [
        {
          heading: chip.label,
          matches: [],
          emptyNote: b.emptyNote,
        },
      ];
    }
    if (b.kind === "assessmentMeasure") {
      // Task #155 — one group per detected label_name with chunk
      // buttons for each transcript turn that produced it.
      return b.detectedLabels.map((lbl) => {
        const turnSet = new Set(lbl.turns);
        const matches: { idx: number }[] = [];
        segments.forEach((s, idx) => {
          if (s.turn != null && turnSet.has(s.turn)) matches.push({ idx });
        });
        return {
          heading: lbl.name,
          matches,
          emptyNote:
            "No transcript turns were tagged with this label by the evidence extractor.",
        };
      });
    }
    // assessmentLabel — single label group.
    const turnSet = new Set(b.turns);
    const matches: { idx: number }[] = [];
    segments.forEach((s, idx) => {
      if (s.turn != null && turnSet.has(s.turn)) matches.push({ idx });
    });
    return [
      {
        heading: `${b.assessmentName} \u2014 ${b.measureLabel} \u2014 ${b.labelName}`,
        matches,
        emptyNote:
          "No transcript turns were tagged with this label by the evidence extractor.",
      },
    ];
  }, [chip, segments]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      } else if (e.key === "Tab") {
        const root = dialogRef.current;
        if (!root) return;
        const focusables = root.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    queueMicrotask(() => closeBtnRef.current?.focus());
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const totalMatches = groups.reduce((n, g) => n + g.matches.length, 0);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-primary/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-2xl max-h-[80vh] flex flex-col rounded-lg bg-card shadow-lg"
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h3
            id={titleId}
            className="font-semibold text-foreground text-sm"
          >
            {chip.label}
            {" \u2014 transcript segments"}
          </h3>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-muted-foreground hover:text-foreground text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          >
            {"\u2715"}
          </button>
        </div>
        <div className="p-4 overflow-y-auto space-y-4">
          {totalMatches === 0 ? (
            <p className="text-sm text-muted-foreground">
              {groups[0]?.emptyNote ||
                "No transcript segments are tagged with this label."}
            </p>
          ) : (
            groups.map((g, gi) => (
              <div key={gi} className="space-y-2">
                {groups.length > 1 ? (
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {g.heading}
                  </h4>
                ) : null}
                <ul className="space-y-2">
                  {g.matches.map(({ idx }) => {
                    const s = segments[idx];
                    if (!s) return null;
                    return (
                      <li key={idx}>
                        <button
                          type="button"
                          onClick={() => onJump(idx)}
                          className="w-full text-left rounded border border-border bg-card hover:bg-muted/50 px-3 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <div className="flex items-baseline gap-2 flex-wrap">
                            <span className="text-xs font-semibold text-foreground">
                              {s.speaker || "Unknown"}
                            </span>
                            {s.time ? (
                              <span className="text-[10px] text-muted-foreground tabular-nums">
                                {s.time}
                              </span>
                            ) : null}
                            {s.turn != null ? (
                              <span className="text-[10px] text-muted-foreground tabular-nums">
                                #{s.turn}
                              </span>
                            ) : null}
                          </div>
                          {s.text ? (
                            <p className="text-sm text-foreground mt-0.5 whitespace-pre-wrap">
                              {s.text}
                            </p>
                          ) : null}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
